import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { formatSessionDate } from '@/lib/datetime';
import { RecordingLink } from '@/pages/cade/RecordingLink';
import { Stat } from '@/pages/cade/Stat';
import { WindowCaption } from '@/pages/cade/WindowCaption';
import { fetchCadeSessions } from '@/services/cade';
import type { CadeSession, CadeSessionArchive } from '@/services/types';
import { ArchiveSources } from '@/pages/cade/WindowCaption';

const PAGE_SIZE = 50;

/**
 * The session archive, one row per ata, newest archive year first.
 *
 * What a reader following the Tribunal opens first: which sessions sat, and
 * what each decided -- counted per session, never rated against another. Every
 * row opens the decision search narrowed to that session, which is where the
 * items themselves are read. A session is one minutes FILE: the archive year
 * on the strip is the folder it is filed under, the number and the long-form
 * date are what the file says, and the reach panel states how many carry each.
 */
export function CadeSessionsPage() {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);

  const { data, isPending, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['cade', 'sessions'],
      queryFn: ({ pageParam }) => fetchCadeSessions({ limit: PAGE_SIZE, offset: pageParam }),
      initialPageParam: 0,
      // Driven by the server's own total, never by "the last page came back
      // full": a result set that is an exact multiple of the page size would
      // otherwise leave a button promising rows that do not exist.
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
        return loaded < lastPage.total ? loaded : undefined;
      },
    });
  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const first = data?.pages[0];
  const items = data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('cade:sessions.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('cade:sessions.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="cade:sessions.explainer" />
      {first ? <AsOfStrip asOf={first.as_of} /> : null}

      {first ? (
        <section className="rounded-lg border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold text-foreground">
            {t('cade:sessions.archive.title')}
          </h2>
          <Answer answer={first.archive} help={t('cade:sessions.archive.help')}>
            {(archive: CadeSessionArchive) => <ArchivePanel archive={archive} />}
          </Answer>
          <div className="mt-2">
            <WindowCaption window={first.as_of.year_window} />
          </div>
        </section>
      ) : null}

      <div className="flex justify-end">
        <ExportButton path="/api/v1/cade/export/sessions" />
      </div>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : !first ? (
        <SkeletonLoader rows={8} />
      ) : (
        <>
          <p className="text-xs text-muted-foreground" aria-live="polite" data-total={first.total}>
            {t('cade:sessions.showing', {
              loaded: number.format(items.length),
              total: number.format(first.total),
            })}
          </p>
          <div
            ref={scrollRef}
            className="max-h-[65vh] overflow-auto rounded-lg border"
            role="region"
            aria-label={t('cade:sessions.tableLabel')}
          >
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('cade:sessions.columns.year')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:sessions.columns.session')}</th>
                  <th className="px-3 py-2 font-medium">{t('cade:sessions.columns.date')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:sessions.columns.decisions')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:sessions.columns.cases')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:sessions.columns.withFines')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('cade:sessions.columns.withVotes')}
                  </th>
                  <th className="px-3 py-2 font-medium">{t('cade:sessions.columns.ata')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <SessionRow key={item.session_key} item={item} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} aria-hidden className="h-px" />
            <div className="flex justify-center py-3">
              {hasNextPage ? (
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={isFetchingNextPage}
                  className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
                >
                  {isFetchingNextPage
                    ? t('cade:cases.loadingMore')
                    : t('cade:sessions.loadMore', { count: PAGE_SIZE })}
                </button>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {t('cade:sessions.allLoaded', { total: number.format(first.total) })}
                </p>
              )}
            </div>
          </div>
          <FieldGuide i18nKey="cade:sessions.guide" />
        </>
      )}
    </div>
  );
}

function ArchivePanel({ archive }: { archive: CadeSessionArchive }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
        <Stat label={t('cade:sessions.archive.sessions')} value={number.format(archive.sessions)} />
        <Stat
          label={t('cade:sessions.archive.numbered')}
          value={`${number.format(archive.with_number)} / ${number.format(archive.sessions)}`}
        />
        <Stat
          label={t('cade:sessions.archive.dated')}
          value={`${number.format(archive.with_date)} / ${number.format(archive.sessions)}`}
        />
        <Stat label={t('cade:sessions.archive.decisions')} value={number.format(archive.decisions)} />
      </dl>
      <ArchiveCoverage archive={archive} />
    </div>
  );
}

/**
 * Where the archive is thin, computed from the sessions themselves: sessions
 * per year folder, and the ordinary-session numbers that fall inside the
 * numbered span and that no ata carries. Nothing here is typed in a locale
 * string, so a reload that fills a year changes the figures with it.
 */
function ArchiveCoverage({ archive }: { archive: CadeSessionArchive }) {
  const { t } = useTranslation(['cade']);
  if (archive.by_year.length === 0 && archive.ordinary_first === null) return null;
  return (
    <div className="space-y-1 text-xs text-muted-foreground" data-testid="archive-coverage">
      <ArchiveSources sources={archive.by_source} />
      {archive.by_year.length > 0 ? (
        <p>
          {t('cade:sessions.archive.byYear')}{' '}
          {archive.by_year.map((y, index) => (
            <span key={y.year} className="tabular-nums text-foreground">
              {index > 0 ? ' · ' : ''}
              {y.year}: {y.sessions}
            </span>
          ))}
        </p>
      ) : null}
      {archive.ordinary_first !== null && archive.ordinary_last !== null ? (
        <p>
          {archive.missing_numbers.length === 0
            ? t('cade:sessions.archive.noGaps', {
                first: archive.ordinary_first,
                last: archive.ordinary_last,
              })
            : t('cade:sessions.archive.gaps', {
                first: archive.ordinary_first,
                last: archive.ordinary_last,
                count: archive.missing_numbers.length,
                numbers: archive.missing_numbers.join(', '),
              })}
        </p>
      ) : null}
    </div>
  );
}

function SessionRow({ item }: { item: CadeSession }) {
  const { t, i18n } = useTranslation(['cade']);
  const number = new Intl.NumberFormat(i18n.language);
  const file = item.source_file.split(/[\\/]/).pop() ?? item.source_file;

  return (
    <tr className="border-t align-top hover:bg-muted/30">
      <td className="px-3 py-2 tabular-nums text-foreground">{item.session_year ?? '—'}</td>
      <td className="px-3 py-2">
        {/* The row's link goes to the decisions of THIS session: the items are
            what a reader wants, and the search page already draws them. */}
        <Link
          to={`/cade/decisions?session=${encodeURIComponent(item.session_key)}`}
          className="text-primary underline-offset-4 hover:underline"
        >
          {item.session_number
            ? t('cade:sessions.numbered', { number: item.session_number })
            : t('cade:sessions.unnumbered')}
        </Link>
        {item.session_type ? (
          <span className="ml-2 text-xs text-muted-foreground">{item.session_type}</span>
        ) : null}
        {item.source_kind === 'sei_reconstructed' ? (
          <span
            className="ml-2 rounded border border-dashed px-1.5 text-xs text-foreground"
            title={t('cade:archiveSources.badgeTitle')}
            data-session-source="sei_reconstructed"
          >
            {t('cade:archiveSources.badge')}
          </span>
        ) : null}
      </td>
      {/* The parsed date where there is one; the ata's prose stays in the tooltip when it
          says something else, so a reader can see the two side by side. */}
      <td
        className="px-3 py-2 text-xs text-muted-foreground"
        title={item.session_date && item.session_date_raw ? item.session_date_raw : undefined}
        data-session-date={item.session_date ?? undefined}
      >
        {formatSessionDate(item.session_date, item.session_date_raw, i18n.language) ?? '—'}
        <div>
          <RecordingLink date={item.session_date} sessionType={item.session_type} />
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-foreground">
        {number.format(item.decisions)}
        <span className="text-muted-foreground">
          {' '}
          · {t('cade:sessions.final', { count: item.final_decisions })}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {number.format(item.cases)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {number.format(item.with_fines)}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
        {number.format(item.with_votes)}
      </td>
      {/* The one source document a session IS: the ata file, by name, with the
          archive path in the tooltip and in the CSV. */}
      <td className="px-3 py-2 text-xs text-muted-foreground" title={item.source_file}>
        {file}
      </td>
    </tr>
  );
}
